/** Operator calendar — Phase 1B-L (2026-05-30).
 *
 * Month + week views fed by `GET /v1/sessions/calendar?from=&to=`.
 * That endpoint buckets sessions by `scheduled_for` when present and
 * falls back to `created_at` for ad-hoc sessions, so the row's
 * `occurs_at` is always the date you'd expect to see it on. (The
 * older sibling endpoint `GET /v1/sessions?from=&to=` filters by
 * `created_at` only — fine for "what happened this month" but wrong
 * for future bookings whose schedule outruns the request window.)
 *
 * Each cell shows ≤3 chips + a "+N more" link; chips are clickable
 * and navigate to the session page. The "view all" affordance on a
 * busy day deep-links into the filtered list view.
 *
 * Weekday order: ISO Monday-first. Locale-aware first-day-of-week
 * is a follow-up — would need Intl.Locale.weekInfo, partial browser
 * support today.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { PermissionGate } from "@/components/PermissionGate";
import { StatusPill } from "@/components/StatusPill";
import { ApiPaths, api } from "@/lib/api";
import { formatDayKey, formatTime } from "@/lib/format";
import { displayPhone } from "@/lib/phone";
import type { Campaign, Paginated, SessionStatus } from "@/lib/types";
import { SessionDetailDrawer } from "@/modules/sessions";


/** Row shape returned by `/v1/sessions/calendar`. Kept narrow — the
 *  full Session model has ~30 fields and we only need these here. */
type CalendarRow = {
  id: string;
  occurs_at: string;
  scheduled: boolean;
  status: SessionStatus;
  title: string;
  campaign_name: string | null;
  operator_email: string | null;
  /** The beneficiary's phone, when the session is campaign-linked — so
   *  the operator can call them straight from the day view instead of
   *  clicking through first (2026-09-17 backlog). Null for standalone
   *  sessions or a beneficiary with no phone on file. */
  beneficiary_phone: string | null;
  href: string;
};

type CalendarResponse = { results: CalendarRow[] };

type ViewMode = "month" | "week" | "day";


export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<ViewMode>("month");
  // 2026-09-17: a calendar chip/row used to navigate straight into the
  // live call room (`s.href`) no matter the session's status — clicking
  // a merely-scheduled session jumped into an empty waiting room instead
  // of showing who it's with, when, and with whom. Every click now opens
  // the same detail drawer the sessions list uses; the drawer itself is
  // where "join the call" actually lives as an explicit action.
  const [detailSessionId, setDetailSessionId] = useState<string | null>(null);
  // `cursor` is the anchor for the displayed grid:
  //   - month view: any day inside the target month
  //   - week view:  any day inside the target ISO week
  // The cursor is anchored to the org tz (via `orgToday`) so the grid opens
  // on — and navigates through — the org's months/days, not the browser's.
  // All grid-cell day keys derive from it, so they line up with the org-tz
  // event buckets below.
  const [cursor, setCursor] = useState<Date>(() => orgToday());

  // ── Filter state (URL-synced for shareable views) ────────────────
  // The page reads `?campaign=<uuid>` + `?operator=<email>` on mount
  // (so a deep link from /campaigns/:id lands pre-filtered) and
  // writes back via `setSearchParams` whenever the operator changes
  // the dropdowns. Filtering is purely client-side — the calendar
  // endpoint already returns campaign + operator per row.
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaignFilter, setCampaignFilter] = useState<string>(
    () => searchParams.get("campaign") ?? "",
  );
  const [operatorFilter, setOperatorFilter] = useState<string>(
    () => searchParams.get("operator") ?? "",
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (campaignFilter) next.set("campaign", campaignFilter);
    else next.delete("campaign");
    if (operatorFilter) next.set("operator", operatorFilter);
    else next.delete("operator");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // We deliberately only depend on the two filter values — pulling
    // `searchParams` in here would trigger a feedback loop with the
    // setter above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignFilter, operatorFilter]);

  // Campaigns roster — small list, fetched once. Lets the operator
  // pre-filter to a campaign even when its sessions are outside the
  // currently-visible date range.
  const campaignsQ = useQuery({
    queryKey: ["calendar-campaigns-roster"],
    queryFn: async () => {
      const res = await api.get<Paginated<Campaign>>(ApiPaths.campaigns, {
        params: { page_size: "200" },
      });
      return res.data.results ?? [];
    },
    staleTime: 60_000,
  });

  const { rangeFrom, rangeTo, gridDays, headerLabel } = useMemo(() => {
    if (mode === "day") {
      const dayStart = startOfDay(cursor);
      const end = endOfDay(cursor);
      const label = cursor.toLocaleDateString(i18n.language, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      return {
        rangeFrom: dayStart,
        rangeTo: end,
        gridDays: [dayStart],
        headerLabel: label,
      };
    }
    if (mode === "week") {
      const weekStart = mondayBefore(cursor);
      const weekEnd = addDays(weekStart, 6);
      const end = endOfDay(weekEnd);
      const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      const label =
        weekStart.toLocaleDateString(i18n.language, {
          month: "short",
          day: "numeric",
        }) +
        " – " +
        weekEnd.toLocaleDateString(i18n.language, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      return { rangeFrom: weekStart, rangeTo: end, gridDays: days, headerLabel: label };
    }
    const monthStart = firstOfMonth(cursor);
    const gridStart = mondayBefore(monthStart);
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const monthEnd = endOfDay(lastOfMonth(cursor));
    const label = cursor.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
    });
    return { rangeFrom: gridStart, rangeTo: monthEnd, gridDays: days, headerLabel: label };
  }, [mode, cursor, i18n.language]);

  const q = useQuery({
    queryKey: ["sessions-calendar", mode, rangeFrom.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        from: rangeFrom.toISOString(),
        to: rangeTo.toISOString(),
      });
      const res = await api.get<CalendarResponse>(
        `${ApiPaths.sessionsCalendar}?${params.toString()}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  // Operator dropdown options are derived from whatever's on the
  // current page of results — keeps the surface to one fetch and the
  // list stays meaningful (no stale ex-operators with zero bookings).
  const operatorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of q.data?.results ?? []) {
      if (s.operator_email) set.add(s.operator_email);
    }
    return Array.from(set).sort();
  }, [q.data]);

  const filteredResults = useMemo(() => {
    const rows = q.data?.results ?? [];
    if (!campaignFilter && !operatorFilter) return rows;
    const wantedCampaignName = campaignFilter
      ? (campaignsQ.data ?? []).find((c) => c.id === campaignFilter)?.name ?? null
      : null;
    return rows.filter((s) => {
      if (campaignFilter && s.campaign_name !== wantedCampaignName) return false;
      if (operatorFilter && s.operator_email !== operatorFilter) return false;
      return true;
    });
  }, [q.data, campaignFilter, operatorFilter, campaignsQ.data]);

  // Backlog 2026-09-21 — `filteredResults` mixes real appointments
  // (`scheduled: true`) with wave invites the beneficiary hasn't picked
  // a slot for yet (`scheduled: false`, listed under the day they were
  // SENT so the operator can see the wave landed, not because they're
  // "at" any particular time). A single blended count reads as "60
  // appointments" when it's really e.g. 12 real ones + 48 pending
  // invites — split it so that's never ambiguous.
  const scheduledCount = useMemo(
    () => filteredResults.filter((s) => s.scheduled).length,
    [filteredResults],
  );
  const pendingCount = filteredResults.length - scheduledCount;

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, CalendarRow[]>();
    for (const s of filteredResults) {
      const key = formatDayKey(s.occurs_at);
      const bucket = map.get(key) ?? [];
      bucket.push(s);
      map.set(key, bucket);
    }
    return map;
  }, [filteredResults]);

  const weekdayKeys = [
    "calendar.weekdayMon",
    "calendar.weekdayTue",
    "calendar.weekdayWed",
    "calendar.weekdayThu",
    "calendar.weekdayFri",
    "calendar.weekdaySat",
    "calendar.weekdaySun",
  ];

  const todayKey = formatDayKey(new Date());

  const stepBack = () =>
    setCursor((c) =>
      mode === "day"
        ? addDays(c, -1)
        : mode === "week"
          ? addDays(c, -7)
          : addMonths(c, -1),
    );
  const stepForward = () =>
    setCursor((c) =>
      mode === "day"
        ? addDays(c, 1)
        : mode === "week"
          ? addDays(c, 7)
          : addMonths(c, 1),
    );

  return (
    <AppLayout
      title={t("nav.calendar")}
      description={t("calendar.description")}
      headerAction={
        <div className="flex items-center gap-2">
          {/* Month/week toggle */}
          <div className="flex rounded-md border border-nb-border bg-nb-surface/30 p-0.5">
            <button
              type="button"
              className={
                "rounded px-2 py-1 text-xs font-medium " +
                (mode === "month"
                  ? "bg-nb-mint/20 text-nb-text"
                  : "text-nb-text-2 hover:text-nb-text")
              }
              onClick={() => setMode("month")}
            >
              {t("calendar.viewMonth")}
            </button>
            <button
              type="button"
              className={
                "rounded px-2 py-1 text-xs font-medium " +
                (mode === "week"
                  ? "bg-nb-mint/20 text-nb-text"
                  : "text-nb-text-2 hover:text-nb-text")
              }
              onClick={() => setMode("week")}
            >
              {t("calendar.viewWeek")}
            </button>
            <button
              type="button"
              className={
                "rounded px-2 py-1 text-xs font-medium " +
                (mode === "day"
                  ? "bg-nb-mint/20 text-nb-text"
                  : "text-nb-text-2 hover:text-nb-text")
              }
              onClick={() => setMode("day")}
            >
              {t("calendar.viewDay", "Day")}
            </button>
          </div>
          <button
            type="button"
            className="btn-ghost text-sm"
            aria-label={t("calendar.previous")}
            onClick={stepBack}
          >
            ←
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setCursor(orgToday())}
          >
            {t("calendar.today")}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            aria-label={t("calendar.next")}
            onClick={stepForward}
          >
            →
          </button>
        </div>
      }
    >
      <PermissionGate
        perm="session.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            {t("admin.noPermission")}
          </p>
        }
      >
        <section className="card">
          <header className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold capitalize text-nb-text">
              {headerLabel}
            </h2>
            <span className="text-xs text-nb-text-3">
              {pendingCount > 0
                ? t("calendar.countSessionsSplit", {
                    defaultValue:
                      "{{scheduled}} appointments · {{pending}} awaiting booking",
                    scheduled: scheduledCount,
                    pending: pendingCount,
                  })
                : t("calendar.countSessions", {
                    count: filteredResults.length,
                  })}
            </span>
          </header>

          {/* Phase 2E-cal-filt — campaign + operator filters. URL-synced
              so a deep link from /campaigns/:id lands on a pre-filtered
              view. Filtering is client-side over the loaded month/week
              (matches the existing one-fetch model). */}
          <div className="mb-4 flex flex-wrap items-end gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-nb-text-2">
                {t("calendar.filterCampaign", "Campaign")}
              </span>
              <select
                className="input"
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
                aria-label={t("calendar.filterCampaignAriaLabel", "Filter by campaign")}
              >
                <option value="">
                  {t("calendar.filterAllCampaigns", "All campaigns")}
                </option>
                {(campaignsQ.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-nb-text-2">
                {t("calendar.filterOperator", "Operator")}
              </span>
              <select
                className="input"
                value={operatorFilter}
                onChange={(e) => setOperatorFilter(e.target.value)}
                aria-label={t("calendar.filterOperatorAriaLabel", "Filter by operator")}
              >
                <option value="">
                  {t("calendar.filterAllOperators", "All operators")}
                </option>
                {operatorOptions.map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </label>
            {(campaignFilter || operatorFilter) && (
              <button
                type="button"
                className="btn-ghost h-[34px] text-xs text-nb-text-2 hover:text-nb-text"
                onClick={() => {
                  setCampaignFilter("");
                  setOperatorFilter("");
                }}
              >
                {t("calendar.filterClear", "Clear filters")}
              </button>
            )}
          </div>

          {/* Day view = an agenda list for the single day; month/week = grid. */}
          {mode === "day" ? (
            <DayAgenda
              sessions={sessionsByDay.get(ymd(cursor)) ?? []}
              t={t}
              onOpenSession={setDetailSessionId}
            />
          ) : (
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-nb-border bg-nb-surface/60 text-xs">
            {weekdayKeys.map((k) => (
              <div
                key={k}
                className="bg-nb-surface/30 px-2 py-2 text-center font-medium uppercase tracking-wider text-nb-text-2"
              >
                {t(k)}
              </div>
            ))}
            {gridDays.map((d) => {
              const key = ymd(d);
              const sessions = sessionsByDay.get(key) ?? [];
              const inMonth =
                mode === "week" || d.getMonth() === cursor.getMonth();
              const isToday = key === todayKey;
              return (
                <DayCell
                  key={key}
                  date={d}
                  sessions={sessions}
                  inMonth={inMonth}
                  isToday={isToday}
                  tall={mode === "week"}
                  t={t}
                  onOpenDay={(day) => {
                    setCursor(new Date(day));
                    setMode("day");
                  }}
                  onOpenSession={setDetailSessionId}
                />
              );
            })}
          </div>
          )}

          {q.isLoading && (
            <p className="mt-4 text-sm text-nb-text-2">{t("dashboard.loading")}</p>
          )}
          {/* Was previously silent — a fetch failure left the grid/day
              view empty with no way to tell "nothing scheduled" from
              "the calendar failed to load". */}
          {q.isError && (
            <p className="mt-4 text-sm text-cvp-danger">
              {t("calendar.loadError", "Could not load the calendar.")}
            </p>
          )}
          {mode !== "day" &&
            q.data &&
            filteredResults.length === 0 &&
            !q.isLoading && (
              <p className="mt-4 text-sm text-nb-text-2">
                {campaignFilter || operatorFilter
                  ? t("calendar.emptyFiltered", "No sessions match these filters in this range.")
                  : t("calendar.emptyMonth")}
              </p>
            )}
        </section>

        {detailSessionId && (
          <SessionDetailDrawer
            key={detailSessionId}
            sessionId={detailSessionId}
            onClose={() => setDetailSessionId(null)}
          />
        )}
      </PermissionGate>
    </AppLayout>
  );
}


function DayCell({
  date,
  sessions,
  inMonth,
  isToday,
  tall,
  t,
  onOpenDay,
  onOpenSession,
}: {
  date: Date;
  sessions: CalendarRow[];
  inMonth: boolean;
  isToday: boolean;
  tall: boolean;
  t: ReturnType<typeof useTranslation>["t"];
  onOpenDay: (date: Date) => void;
  onOpenSession: (id: string) => void;
}) {
  const visible = sessions.slice(0, tall ? 8 : 3);
  const hidden = sessions.length - visible.length;

  return (
    // Clicking the cell opens that day's day view. The session buttons
    // and "+N more" stopPropagation so they keep their own action.
    //
    // The count badge and "+N more" used to be <Link>s to
    // `/sessions?from=…&to=…`. That went nowhere useful: SessionsTable
    // reads no URL parameters, so the day range was dropped — and
    // `/sessions` is the STANDALONE list, which by definition cannot
    // show the campaign sessions that fill this calendar, nor honour
    // the campaign/operator filters set above it. Both now open the day
    // view, which lists exactly the sessions the cell is counting.
    <div
      role="button"
      tabIndex={0}
      title={t("calendar.openDay", "Open day view")}
      onClick={() => onOpenDay(date)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenDay(date);
        }
      }}
      className={
        (tall ? "min-h-[220px] " : "min-h-[88px] ") +
        "cursor-pointer bg-nb-bg/60 p-2 hover:bg-nb-surface/40 " +
        "focus:outline-none focus:ring-1 focus:ring-nb-mint " +
        (inMonth ? "" : "opacity-40 ")
      }
    >
      <div
        className={
          "mb-1 flex items-center justify-between text-xs " +
          (isToday ? "font-bold text-nb-mint" : "font-medium text-nb-text")
        }
      >
        <span>{date.getDate()}</span>
        {sessions.length > 0 && (
          <span
            className="text-[10px] text-nb-text-3"
            title={t("calendar.viewListFiltered", { count: sessions.length })}
          >
            {sessions.length}
          </span>
        )}
      </div>
      <div className="space-y-1">
        {visible.map((s) => (
          <button
            key={s.id}
            type="button"
            className="block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] hover:bg-slate-700/40"
            title={`${s.title}${s.campaign_name ? ` — ${s.campaign_name}` : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenSession(s.id);
            }}
          >
            <StatusPill status={s.status} />
            <span className="ml-1 truncate text-nb-text">{s.title}</span>
          </button>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            className="block w-full px-1.5 text-left text-[10px] text-nb-text-2 hover:text-nb-text"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDay(date);
            }}
          >
            {t("calendar.moreCount", { count: hidden })}
          </button>
        )}
      </div>
    </div>
  );
}


/** Agenda-style single-day view: the day's sessions in time order, with
 * fuller detail than a month/week grid cell affords. */
function DayAgenda({
  sessions,
  t,
  onOpenSession,
}: {
  sessions: CalendarRow[];
  t: ReturnType<typeof useTranslation>["t"];
  onOpenSession: (id: string) => void;
}) {
  // Backlog 2026-09-21 — a wave-invited beneficiary who hasn't picked a
  // slot yet (`scheduled: false`) was rendered as an ordinary time-slot
  // row using `occurs_at` (which falls back to when the invite was
  // SENT, not any real appointment time). A big wave landing at once
  // made dozens of unrelated invites look like the same operator was
  // double/triple/quadruple-booked at the exact same minute. Split them
  // into their own section with no fabricated time chip.
  const scheduledRows = sessions
    .filter((s) => s.scheduled)
    .sort(
      (a, b) => new Date(a.occurs_at).getTime() - new Date(b.occurs_at).getTime(),
    );
  const pendingRows = sessions.filter((s) => !s.scheduled);

  if (scheduledRows.length === 0 && pendingRows.length === 0) {
    return (
      <div className="rounded-lg border border-nb-border bg-nb-bg/60 p-6 text-center text-sm text-nb-text-3">
        {t("calendar.dayEmpty", "No sessions on this day.")}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {scheduledRows.length > 0 && (
        <ul className="space-y-2">
          {scheduledRows.map((s) => (
            <AgendaRow key={s.id} s={s} t={t} onOpenSession={onOpenSession} />
          ))}
        </ul>
      )}
      {pendingRows.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
            {t("calendar.pendingSectionTitle", {
              defaultValue: "Awaiting self-booking ({{count}})",
              count: pendingRows.length,
            })}
          </h3>
          <p className="text-xs text-nb-text-3">
            {t(
              "calendar.pendingSectionHint",
              "These beneficiaries were sent a booking invite on this day — they haven't picked a slot yet. Not real appointments.",
            )}
          </p>
          <ul className="space-y-2">
            {pendingRows.map((s) => (
              <AgendaRow
                key={s.id}
                s={s}
                t={t}
                onOpenSession={onOpenSession}
                pending
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AgendaRow({
  s,
  t,
  onOpenSession,
  pending,
}: {
  s: CalendarRow;
  t: ReturnType<typeof useTranslation>["t"];
  onOpenSession: (id: string) => void;
  pending?: boolean;
}) {
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={() => onOpenSession(s.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenSession(s.id);
        }
      }}
      className={
        "group flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-3 transition-colors hover:border-nb-mint/40 hover:bg-nb-surface/40 focus:outline-none focus:ring-1 focus:ring-nb-mint " +
        (pending
          ? "border-dashed border-nb-border/70 bg-nb-bg/30"
          : "border-nb-border bg-nb-bg/60")
      }
    >
      {/* The row itself opens the session detail drawer; the phone
          link below is a SIBLING (not nested inside this div) — it
          stops propagation so it keeps its own click-to-call action
          instead of also opening the drawer. */}
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {/* Time — the one thing that needs to read instantly, so it
            gets its own accent chip rather than blending into the
            row's body text. A pending (not-yet-booked) row gets a
            badge instead — showing a clock time here would claim an
            appointment that doesn't exist yet. */}
        {pending ? (
          <div className="flex w-16 shrink-0 flex-col items-center rounded-md bg-nb-warn/10 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-nb-warn">
              {t("calendar.pendingBadge", "Awaiting")}
            </span>
          </div>
        ) : (
          <div className="flex w-16 shrink-0 flex-col items-center rounded-md bg-nb-mint/10 py-1.5 tabular-nums">
            <span className="text-sm font-semibold text-nb-mint">
              {formatTime(s.occurs_at, { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-nb-text">
              {s.title}
            </span>
            <StatusPill status={s.status} />
          </div>
          {/* Campaign name is context, not the headline — kept small
              and muted so it doesn't compete with who the appointment
              is actually with. */}
          {s.campaign_name && (
            <p className="truncate text-xs text-nb-text-3">
              {s.campaign_name}
            </p>
          )}
        </div>
      </div>

      {/* Phone — the operator's stated need ("I don't have his
          telephone number") answered right on the row, as a
          click-to-call link that doesn't also navigate. */}
      {s.beneficiary_phone && (
        <a
          href={`tel:${s.beneficiary_phone}`}
          className="hidden shrink-0 items-center gap-1.5 rounded-md border border-nb-border px-2.5 py-1.5 text-xs font-medium text-nb-text-2 hover:border-nb-mint/50 hover:text-nb-mint sm:flex"
          title={t("calendar.callBeneficiary", "Call")}
          onClick={(e) => e.stopPropagation()}
        >
          <PhoneIcon />
          {displayPhone(s.beneficiary_phone)}
        </a>
      )}
      {s.operator_email && (
        <span className="hidden shrink-0 text-xs text-nb-text-3 lg:inline">
          {s.operator_email}
        </span>
      )}
    </li>
  );
}

function PhoneIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.5 4.5c0-.55.45-1 1-1h2.19c.46 0 .86.31.97.76l.73 2.9a1 1 0 0 1-.27.98l-1.4 1.4a11.5 11.5 0 0 0 5.24 5.24l1.4-1.4a1 1 0 0 1 .98-.27l2.9.73c.45.11.76.51.76.97v2.19c0 .55-.45 1-1 1h-1C7.94 18 2 12.06 2 4.5v-1Z"
      />
    </svg>
  );
}


// ─── date helpers (no deps — keep this surface tight) ─────────────

/** "Today" as a local Date positioned on the ORG tz's calendar day, so the
 * grid opens on the org's month/day. Time-of-day is irrelevant — the grid
 * helpers only read Y/M/D. Near a tz day/month boundary this differs from the
 * browser's `new Date()`. */
function orgToday(): Date {
  const [y, m, d] = formatDayKey(new Date()).split("-");
  return new Date(Number(y), Number(m) - 1, Number(d));
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function lastOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
}
function mondayBefore(d: Date): Date {
  const day = d.getDay(); // 0 = Sun … 6 = Sat
  const delta = (day + 6) % 7; // Monday = 0
  const out = addDays(d, -delta);
  out.setHours(0, 0, 0, 0);
  return out;
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
